import type { Product } from 'app/features/recommend/interfaces/models';
import type { RefObject } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useIntersection } from '~/hooks/useIntersection';
import type { CommonImpressionFieldObject, SendImpressionType } from '~/utils/gtag';
import { commonSendGAImpression } from '~/utils/gtag';

export type ImpListName =
  | 'home_banner'
  | 'home_timeline'
  | 'graph_tooltip_length'
  | 'graph_tooltip_circumference'
  | 'graph_tooltip_height'
  | 'graph_tooltip_weight'
  | 'event_replacement'
  | 'measure_recommended'
  | 'home_modal_recommend'
  | 'growth_modal_recommend'
  | 'predict_modal_recommend';

export const useCommonProductsImpression = (
  listName: ImpListName | undefined,
  observer: RefObject<HTMLDivElement>,
  products: Product[],
) => {
  const isIntersecting = useIntersection(observer);
  const [recentlySent, setRecentlySent] = useState<string>('');
  const brandMapping: { [key: string]: string } = {
    'www.asics.com': 'ASICS',
    'www.asics-trading.co.jp': 'ATR',
  };
  const impData: CommonImpressionFieldObject[] = useMemo(() => {
    return products
      .map((product, i) => {
        if (!product) return;
        const url = new URL(product.link);
        const obj: CommonImpressionFieldObject = {
          item_id: product.product_id.toString(),
          item_name: product.name,
          item_hostname: url.hostname,
          item_list_id: listName ?? '',
          item_list_name: listName ?? '',
          index: i + 1,
          item_variant: product.size.value.toString(),
          price: product.price ?? 0,
          affiliation: 'stepnote',
          item_brand: brandMapping[url.hostname] || '',
          item_category: product.item_category ?? '',
          item_size: product.size.value,
          currency: product.currency ?? '',
          quantity: 1,
        };
        return obj;
      })
      .filter((v) => v) as CommonImpressionFieldObject[];
  }, [listName, products]);

  useEffect(() => {
    if (!listName) return;
    if (isIntersecting && impData.length && listName !== recentlySent) {
      let type: SendImpressionType = 'view_item_list';
      if (listName.includes('home_banner')) type = 'view_item_list_home';
      if (listName.includes('home_timeline')) type = 'view_item_list_timeline';
      if (listName.includes('measure_')) type = 'view_item_list_measure';
      if (listName.includes('event_')) type = 'view_item_list_event';
      if (listName.includes('graph_')) type = 'view_item_list_graph';
      if (listName.includes('growth_')) type = 'view_item_list_growth';

      commonSendGAImpression({ items: impData, type: type });
      setRecentlySent(listName);
    }
  }, [isIntersecting, impData]);
};
